import { listAllBlobs, type BlobEntry } from '~lib/storage/abs-client';

export interface DatasetFile {
  // Path within the dataset (e.g. `sub/b.parquet` for blob `foo/sub/b.parquet`).
  path: string;
  // Full blob name including the dataset prefix — what ABS calls a blob.
  fullPath: string;
  size: number;
}

export interface Dataset {
  name: string;
  fileCount: number;
  totalBytes: number;
  files: DatasetFile[];
  // The pre-packaged archive at `<name>/<name>.zip` if it exists. Treated as
  // a packaging artifact rather than a content file: excluded from `files`,
  // `fileCount`, `totalBytes`, and the folder-download iteration.
  zipFile: DatasetFile | null;
}

// Aggregates a flat blob listing into datasets keyed by the first path segment.
// E.g. blobs `foo/a.csv`, `foo/sub/b.csv`, `bar/c.parquet` → datasets `foo`
// (2 files) and `bar` (1 file). Blobs at the container root with no `/` are
// skipped — they're not part of a dataset by this convention.
//
// A blob at the well-known path `<name>/<name>.zip` is split off as the
// dataset's `zipFile` and otherwise hidden — the catalogue treats it as the
// packaged form of the same content the loose files represent.
export function aggregateDatasets(blobs: BlobEntry[]): Dataset[] {
  const byName = new Map<string, Dataset>();

  for (const blob of blobs) {
    const slash = blob.name.indexOf('/');
    if (slash <= 0) continue;
    const name = blob.name.slice(0, slash);
    const path = blob.name.slice(slash + 1);
    if (path.length === 0) continue;

    const file: DatasetFile = {
      path,
      fullPath: blob.name,
      size: blob.contentLength,
    };

    let dataset = byName.get(name);
    if (!dataset) {
      dataset = {
        name,
        fileCount: 0,
        totalBytes: 0,
        files: [],
        zipFile: null,
      };
      byName.set(name, dataset);
    }

    if (blob.name === `${name}/${name}.zip`) {
      dataset.zipFile = file;
    } else if (path.startsWith('.metadata/')) {
      // Internal metadata folder — hidden from the catalogue UI entirely.
    } else {
      dataset.fileCount += 1;
      dataset.totalBytes += blob.contentLength;
      dataset.files.push(file);
    }
  }

  for (const dataset of byName.values()) {
    dataset.files.sort((a, b) => a.path.localeCompare(b.path));
  }
  return Array.from(byName.values()).sort((a, b) => a.name.localeCompare(b.name));
}

export async function loadDatasets(token: string): Promise<Dataset[]> {
  const blobs = await listAllBlobs(token);
  return aggregateDatasets(blobs);
}
